import React from "react";
import { useTranslation } from "react-i18next";
import {
  // GestoreMovimentoType, // ribilanciamento hidden (client request 2026-09-07)
  InformazioniGestoreType,
} from "types/AssistantTypes";
import { formatItalianNumber } from "utils/formatUtils";
import styles from "./informazioniGestore.module.scss";

type InformazioniGestoreProps = {
  data: InformazioniGestoreType;
};

// Ribilanciamento del gestore hidden on client request (2026-09-07); data still
// arrives in `data.ribilanciamento` — uncomment this and the block below to restore.
/*
const Movimento: React.FC<{ m: GestoreMovimentoType }> = ({ m }) => {
  const { t } = useTranslation();
  if (m.movimento_kind === "in") {
    return (
      <span className={styles.in}>
        {t("productCard.movimentoIn")} <span className={styles.arrow}>⇤</span>
      </span>
    );
  }
  if (m.movimento_kind === "out") {
    return (
      <span className={styles.out}>
        {t("productCard.movimentoOut")} <span className={styles.arrow}>⇥</span>
      </span>
    );
  }
  const up = m.movimento_kind === "weight_up";
  const pct =
    m.movimento_pct == null
      ? ""
      : ` ${up ? "+" : ""}${formatItalianNumber(m.movimento_pct, 2)}%`;
  return (
    <span className={styles.weight}>
      {t("productCard.movimentoWeight")}{" "}
      <span className={styles.arrow}>{up ? "↑" : "↓"}</span>
      {pct}
    </span>
  );
};
*/

const WidgetTitle: React.FC<{ title: string; date: string }> = ({
  title,
  date,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.widgetHeader}>
      <h3 className={styles.widgetTitle}>{title}</h3>
      <span className={styles.widgetDate}>
        {t("productCard.gestoreData")} {date}
      </span>
    </div>
  );
};

/**
 * «Informazioni dal Gestore» — FAM look-through of a fund of funds (issue #61):
 * Composizione del fondo, Nota del gestore, Ribilanciamento del gestore.
 * Rendered only when the payload carries `informazioni_gestore`.
 */
const InformazioniGestore: React.FC<InformazioniGestoreProps> = ({ data }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.frame}>
      <h2 className={styles.sectionTitle}>
        {t("productCard.informazioniGestore")}
      </h2>
      {data.placeholder ? (
        <>
          <WidgetTitle title="" date={data.data} />
          <p className={styles.muted}>
            {t("productCard.informazioniNonDisponibili")}
          </p>
        </>
      ) : (
        <>
          <WidgetTitle
            title={t("productCard.composizioneFondo")}
            date={data.data}
          />
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>{t("productCard.strumento")}</th>
                <th className={styles.th}>{t("productCard.isin")}</th>
                <th className={styles.th}>{t("productCard.categoria")}</th>
                <th className={`${styles.th} ${styles.right}`}>
                  {t("productCard.peso")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.composizione.map((h) => (
                <tr key={`${h.isin}-${h.strumento}`} className={styles.tr}>
                  <td className={styles.td}>{h.strumento}</td>
                  <td className={styles.td}>{h.isin}</td>
                  <td className={styles.td}>{h.categoria}</td>
                  <td className={`${styles.td} ${styles.right}`}>
                    {formatItalianNumber(h.peso, 2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <WidgetTitle title={t("productCard.notaGestore")} date={data.data} />
          <p className={styles.nota}>
            {data.nota ?? t("productCard.notaNonDisponibile")}
          </p>

          {/* Ribilanciamento del gestore — hidden on client request (2026-09-07)
          <WidgetTitle
            title={t("productCard.ribilanciamentoGestore")}
            date={data.data}
          />
          {data.ribilanciamento.length === 0 ? (
            <p className={styles.muted}>{t("productCard.nessunMovimento")}</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>{t("productCard.strumento")}</th>
                  <th className={styles.th}>{t("productCard.isin")}</th>
                  <th className={styles.th}>{t("productCard.categoria")}</th>
                  <th className={styles.th}>{t("productCard.movimento")}</th>
                </tr>
              </thead>
              <tbody>
                {data.ribilanciamento.map((m) => (
                  <tr key={`${m.isin}-${m.movimento_kind}`} className={styles.tr}>
                    <td className={styles.td}>{m.strumento}</td>
                    <td className={styles.td}>{m.isin}</td>
                    <td className={styles.td}>{m.categoria}</td>
                    <td className={styles.td}>
                      <Movimento m={m} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          */}
        </>
      )}
    </div>
  );
};

export default InformazioniGestore;
